import { Component, Input, OnInit, AfterViewInit, ViewChild, TemplateRef, OnChanges, SimpleChanges } from '@angular/core';
import { DashboardService, Errors, LocalService } from '../../../core';
import { DataTableColumn } from '../../../shared/ui/data-table/data-table.component';
import { splitData, formatNumber } from '../../../shared/utils/format.util';

const MANAGER_COLUMNS: DataTableColumn[] = [
  { field: 'name',                          header: 'Challenge',        align: 'left' },
  { field: 'challengeType',                 header: 'Type',             align: 'center' },
  { field: 'totalSellersParticipating',     header: 'Participants',     align: 'center' },
  { field: 'totalChallengesCompleted',      header: 'Completed',        align: 'center' },
  { field: 'totalBookingsValue',            header: 'Bookings',         align: 'center' },
  { field: 'totalCreditsAvailable',         header: 'Tickets Available', align: 'center' },
  { field: 'totalCreditsRewardedToSellers', header: 'Tickets Earned',   align: 'center', highlight: 'green', isBold: true },
];

const SELLER_COLUMNS: DataTableColumn[] = [
  { field: 'challengeName',   header: 'Challenge',        align: 'center' },
  { field: 'challengeType',   header: 'Type',             align: 'center' },
  { field: 'status',          header: 'Status',           align: 'center' },
  { field: 'bookingsValue',   header: 'Bookings Value',   align: 'center' },
  { field: 'creditsAvailable', header: 'Credits Available', align: 'center' },
  { field: 'creditsEarned',   header: 'Credits Earned',   align: 'center' },
];

@Component({
  selector: 'app-dashboard-activity-challenges',
  templateUrl: './dashboard-activity-challenges.component.html',
  styleUrls: ['./dashboard-activity-challenges.component.scss']
})
export class DashboardActivityChallengesComponent implements OnInit, AfterViewInit, OnChanges {

  @ViewChild('challengeTypeTpl') challengeTypeTpl: TemplateRef<any> = null as any;

  @Input() role: 'manager' | 'seller' = 'seller';
  @Input() dashboardData: any = null;
  @Input() isLoading = false;

  columns: DataTableColumn[] = [];
  isDataLoading = true;
  errors: Errors = { errors: {} };

  // Seller: server-side paginated response
  private sellerData: any = null;
  private userData: any;

  readonly pageSize = 5;
  page = 1;

  constructor(private dashboardService: DashboardService) {}

  ngOnInit() {
    this.userData = LocalService.get('userProfile');
    this.columns = this.role === 'manager' ? MANAGER_COLUMNS : SELLER_COLUMNS;

    if (this.role === 'seller') {
      this.fetchSellerData(this.page);
    } else {
      this.isDataLoading = false;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dashboardData'] && !changes['dashboardData'].firstChange) {
      this.isDataLoading = false;
    }
  }

  ngAfterViewInit() {
    const typeCol = this.columns.find(c => c.field === 'challengeType');
    if (typeCol) {
      typeCol.cellTemplate = this.challengeTypeTpl;
    }
  }

  // ── Manager: rows come from dashboardData passed by parent ────────────────

  get managerFormattedRows(): any[] {
    const challenges = this.dashboardData && this.dashboardData.Challenges;
    if (!challenges) { return []; }
    return challenges.map((challenge: any) => {
      const bookingsValue = Number(challenge.totalBookingsValue) || 0;
      return {
        ...challenge,
        totalSellersParticipating:     splitData(challenge.totalSellersParticipating),
        totalChallengesCompleted:      splitData(challenge.totalChallengesCompleted),
        totalBookingsValue:            bookingsValue === 0 ? 'NA' : '$' + splitData(challenge.totalBookingsValue),
        totalCreditsAvailable:         splitData(challenge.totalCreditsAvailable),
        totalCreditsRewardedToSellers: splitData(challenge.totalCreditsRewardedToSellers),
      };
    });
  }

  get managerPaginatedRows(): any[] {
    const start = (this.page - 1) * this.pageSize;
    return this.managerFormattedRows.slice(start, start + this.pageSize);
  }

  // ── Seller: rows come from its own paginated API call ─────────────────────

  get sellerRows(): any[] {
    const content = this.sellerData && this.sellerData.content ? this.sellerData.content : [];
    return content.map((v: any) => ({
      challengeName:    v.challengeName,
      challengeType:    v.type,
      status:           v.status,
      bookingsValue:    v.bookingsValue === 'N/A' ? 'N/A' : '$' + formatNumber(v.bookingsValue),
      creditsAvailable: formatNumber(v.creditsAvailable),
      creditsEarned:    formatNumber(v.creditsEarned),
    }));
  }

  // ── Unified template bindings ─────────────────────────────────────────────

  get tableLoading(): boolean {
    return this.role === 'manager' ? this.isLoading : this.isDataLoading;
  }

  get rows(): any[] {
    return this.role === 'manager' ? this.managerPaginatedRows : this.sellerRows;
  }

  get totalItems(): number {
    if (this.role === 'manager') {
      return this.managerFormattedRows.length;
    }
    return this.sellerData ? this.sellerData.totalElements : 0;
  }

  get challengeCount(): number {
    if (this.role === 'manager') {
      const challenges = this.dashboardData && this.dashboardData.Challenges;
      return challenges ? challenges.length : 0;
    }
    return this.sellerData ? this.sellerData.totalElements : 0;
  }

  onPageChange(page: number) {
    this.page = page;
    if (this.role === 'seller') {
      this.fetchSellerData(page);
    }
  }

  private fetchSellerData(pageNumber: number) {
    this.isDataLoading = true;
    const params = { page: pageNumber - 1, size: this.pageSize };
    this.dashboardService.getSellerChallenges(this.userData.id, this.userData.company.id, params).subscribe(
      (data: any) => {
        if (data) { this.sellerData = data; }
        this.isDataLoading = false;
      },
      (err: any) => {
        this.errors = err;
        this.isDataLoading = false;
      }
    );
  }
}
